import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {clearSelection} from 'src/components/data-table-selection';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {EntitlementsPagination, NoResultPage, PreparedEntitlementsView, ResourceSuggest, useExpandRoles} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {searchDatasetResources} from 'src/openapi/admin/apis/DatasetEntitlementsResourceSearchApi';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';

import {
    userEntitlementsHeader,
    userEntitlementsLoadingContainer,
} from '../user-entitlements.module.styl';

import {DatasetResourceNameHeader} from './components/dataset-resource-name.header';
import {NO_ASSIGNED_ENTITLEMENT_TITLE, SECTION_NAME, SELECTION_KEY} from './constants';
import {useGetDatasetEntitlements} from './hooks/useGetDatasetEntitlements';
import {userDatasetEntitlementsStateActions, userDatasetEntitlementsStateSelector} from './slices/user-dataset-entitlements-state.slice';
import {userDatasetEntitlementsActions} from './slices/user-dataset-entitlements.slice';

type Props = {
    userId: number,
};

export const DatasetEntitlementsTab = ({userId}: Props) => {
    const dispatch = useDispatch();

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys} = useGetDatasetEntitlements({userId});
    const {
        disabled: isRolesDisabled,
        isExpanded: isRolesExpanded,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(userDatasetEntitlementsActions.clearData());
            dispatch(userDatasetEntitlementsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filterResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(userDatasetEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const noResultMessage = useMemo(() => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <NoResultPage
                    title="No resources found for the selected criteria."
                    description=""
                />
            );
        }

        if (result && !result.data.length) {
            return (
                <NoResultPage
                    title={NO_ASSIGNED_ENTITLEMENT_TITLE}
                    description=""
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    return (
        <>
            <ServerResponseHandler serverError={loadingError} sectionName={SECTION_NAME}>
                <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                    <StickyElement>
                        <div className={userEntitlementsHeader}>
                            <Group justify="space-between">
                                <ResourceSuggest
                                    onSearch={filterResources}
                                    userId={userId}
                                    id="resource-suggest"
                                    searchAction={searchDatasetResources}
                                />
                                <Group alignItems="center">
                                    <Label>Expand Roles</Label>
                                    <StateButton
                                        {...{
                                            name: 'expand-roles',
                                            disabled: isRolesDisabled,
                                            checked: isRolesExpanded,
                                            onChange: toggleRolesExpansion,
                                        }}
                                    />
                                </Group>
                            </Group>
                        </div>
                    </StickyElement>
                    <BoxLoading loading={!result}>
                        <div className={userEntitlementsLoadingContainer}>
                            {result?.data.length ? (
                                <PreparedEntitlementsView
                                    resourceFilter={(cacheKeys.filters.resourceQuery as string | undefined) || ''}
                                    dataTableId="user-dataset-entitlements"
                                    rows={result.data}
                                    selectionKey={SELECTION_KEY}
                                    customOffset={137}
                                    expandedRoleIds={expandedRoleIds}
                                    setExpandedRoleIds={setExpandedRoleIds}
                                    readonly={true}
                                    getColumnWidthByColumnId={getColumnWidthByColumnId}
                                    onStopResize={onStopResize}
                                    paginationStripe={(
                                        <EntitlementsPagination
                                            totalCount={result.totalCount}
                                            stateActions={userDatasetEntitlementsStateActions}
                                            stateSelector={userDatasetEntitlementsStateSelector}
                                        />
                                    )}
                                    shortHeader={<DatasetResourceNameHeader/>}
                                />
                            ) : noResultMessage}
                        </div>
                    </BoxLoading>
                </ErrorBoundary>
            </ServerResponseHandler>
        </>
    );
};
